import { create } from "zustand";

import { ApiError, fetchAuthConfig, fetchMe, verifyOtp } from "~/lib/api";
import {
  endSession,
  establishSession,
  setAuthProvider,
} from "~/lib/auth-session";
import { loadToken } from "~/lib/auth-token";
import type { AuthConfig, AuthUser } from "~/types/auth";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** What the sign-in screen needs to render. Null until `hydrate` has run. */
  config: AuthConfig | null;

  /** On boot: load the auth config and validate any persisted token. */
  hydrate: () => Promise<void>;
  /** Exchange a one-time code for a session and load the account. */
  verify: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called when the API reports the session is no longer valid. */
  handleUnauthorized: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  user: null,
  config: null,

  hydrate: async () => {
    // The config drives the sign-in screen even when nobody is signed in, so a
    // failure here must not be fatal — the screen falls back to its defaults.
    const config = await fetchAuthConfig().catch(() => null);
    if (config) setAuthProvider(config.auth_provider);

    const token = await loadToken();
    if (!token) {
      set({ status: "unauthenticated", user: null, config });
      return;
    }

    try {
      set({ status: "authenticated", user: await fetchMe(), config });
    } catch (error) {
      // Only a rejected credential is a reason to throw the session away. A network
      // blip or a 5xx says nothing about whether the token is still good.
      if (error instanceof ApiError && error.status === 401) {
        await endSession();
      }
      set({ status: "unauthenticated", user: null, config });
    }
  },

  verify: async (email, code) => {
    const session = await verifyOtp(email, code);
    await establishSession(session);
    // The verify response already carries the account, so no second round-trip.
    // Whether it is an *admin* account is AuthGate's decision, not a sign-in error.
    set({ status: "authenticated", user: session.user });
  },

  signOut: async () => {
    await endSession();
    set({ status: "unauthenticated", user: null });
  },

  handleUnauthorized: () => {
    if (get().status === "unauthenticated") return;
    void endSession();
    set({ status: "unauthenticated", user: null });
  },
}));
